import React, { createContext, useContext, useState, useRef, ReactNode, useEffect, useCallback, useMemo } from 'react';
import type { Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';  // ✅ NEW: Import for user ID in webhook notifications
import { useSocket } from './SocketContext';  // ✅ Shared Socket.IO connection (single connection per tab)

import { logger, getErrorMessage } from '../utils/logger';
import { API_BASE_URL } from '../config/api';  // ✅ Industry Standard: Environment-aware URLs
export interface TestStep {
  id: string;
  // Legacy format support
  action?: 'navigate' | 'click' | 'fill' | 'wait' | 'assert' | 'scroll';
  selector?: string;
  value?: string;
  timeout?: number;
  // Enhanced Template format
  templateId?: string;
  parameters?: {
    selector?: string;
    value?: string;
    url?: string;
    options?: string;
    timeout?: number;
    [key: string]: any;
  };
  category?: string;
  lineNumber?: number;
  originalLine?: string;
  description?: string;
}

export interface TestFlow {
  id: string;
  name: string;
  description?: string;
  steps: TestStep[];
  projectId?: string;
  folderId?: string;
  folder?: {
    id: string;
    name: string;
    path: string;
  };
  createdAt: string;
  updatedAt: string;
  status?: 'draft' | 'active' | 'archived' | 'ready';
  processingStatus?: 'recording' | 'parsing' | 'converting' | 'completed';
  isClickable?: boolean;
  type?: string; // 'recorded' | 'generated' | etc.
  recordedAt?: string;
  recordedBy?: string;
  metadata?: {
    totalSteps?: number;
    recordingDuration?: number;
    urls?: string[];
  };
  // Upload file management
  requiresUploadFile?: boolean;
  uploadFilePresent?: boolean;
  uploadFileName?: string;
  uploadFileSize?: number;
  uploadFileUploadedAt?: string;
  // AI test generation fields
  sourceType?: 'codegen' | 'ai-prompt';
  prompt?: string;
  aiModel?: string;
  generatedAt?: string;
}

export interface TestRun {
  id: string;
  flowId: string;
  status: 'running' | 'passed' | 'failed' | 'stopped';
  startTime: string;
  endTime?: string;
  duration?: number;
  logs: string[];
  screenshots: string[];
  errorMessage?: string;
  steps?: Array<{
    id: string;
    templateId?: string;
    status: 'passed' | 'failed' | 'running';
    startTime: string;
    endTime?: string;
    duration?: number;
    description?: string;
    parameters?: any;
    error?: any;
  }>;
  errors?: Array<{
    step: number;
    stepId: string;
    message: string;
    timestamp: string;
  }>;
  aiAnalysis?: {
    rootCause?: string;
    suggestions?: string[];
    confidence?: number;
  };
  // 🚀 NEW: Allure report path for viewing detailed reports
  allureReportPath?: string;
}

interface TestFlowContextType {
  flows: TestFlow[];
  currentFlow: TestFlow | null;
  runs: TestRun[];
  isExecuting: boolean;
  socket: Socket | null;
  user: any;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  setFlows: (flows: TestFlow[]) => void;
  setCurrentFlow: (flow: TestFlow | null | ((prev: TestFlow | null) => TestFlow | null)) => void;
  addFlow: (flow: TestFlow) => void;
  createFlow: (flowData: Partial<TestFlow>) => Promise<TestFlow>;
  updateFlow: (flow: TestFlow) => void;
  cloneFlow: (flowId: string) => Promise<any>;
  deleteFlow: (flowId: string) => Promise<void>;
  executeFlow: (flowId: string) => Promise<void>;
  stopExecution: () => void;
  clearRuns: () => void;
  createFlowFromNaturalLanguage: (text: string) => Promise<TestFlow>;
  refreshData: () => Promise<void>;
  resetKPIs: () => Promise<void>;
  // NEW: Step loading states for bulletproof parameter updates
  stepLoadingStates: {[stepId: string]: boolean};
  setStepLoading: (stepId: string, loading: boolean) => void;
  isStepLoading: (stepId: string) => boolean;
  // 🚀 NEW: Recent runs for dashboard
  fetchRecentRuns: (limit?: number) => Promise<TestRun[]>;
}

const TestFlowContext = createContext<TestFlowContextType | undefined>(undefined);

export const useTestFlow = () => {
  const context = useContext(TestFlowContext);
  if (!context) {
    throw new Error('useTestFlow must be used within a TestFlowProvider');
  }
  return context;
};

interface TestFlowProviderProps {
  children: ReactNode;
}

export const TestFlowProvider: React.FC<TestFlowProviderProps> = ({ children }) => {
  const { user } = useAuth();  // ✅ NEW: Get current user for webhook "Triggered By"
  const { socket: sharedSocket } = useSocket();  // ✅ Shared Socket.IO connection from SocketContext
  
  const [flows, setFlows] = useState<TestFlow[]>([]);
  const [currentFlow, setCurrentFlow] = useState<TestFlow | null>(null);
  const [runs, setRuns] = useState<TestRun[]>([]);
  
  // 🚀 NEW: Fetch recent runs for dashboard
  const fetchRecentRuns = useCallback(async (limit: number = 10) => {
    try {
      logger.debug(`[ATOMIC] Fetching recent runs, limit: ${limit}`);
      const response = await fetch(`${API_BASE_URL}/api/runs/recent?limit=${limit}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch recent runs: ${response.status}`);
      }
      const data = await response.json();
      logger.debug(`[ATOMIC] Fetched ${Array.isArray(data) ? data.length : 0} recent runs`);
      setRuns(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      logger.error('[ATOMIC] Failed to fetch recent runs:', { error: getErrorMessage(error) });
      return [];
    }
  }, []);
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  // ❌ REMOVED: socketRef — now using shared socket from SocketContext
  // ❌ REMOVED: const [user, setUser] = useState<any>(null); - Now using useAuth() instead
  // NEW: Step loading states for bulletproof parameter updates
  const [stepLoadingStates, setStepLoadingStates] = useState<{[stepId: string]: boolean}>({});

  // ✅ FIX: In-flight guard to prevent redundant concurrent refreshData calls
  const isRefreshingRef = useRef(false);

  // Authentication functions
  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      logger.debug('Attempting login with:', { data: email });
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      logger.debug('Login response status:', { data: response.status });

      if (response.ok) {
        const data = await response.json();
        logger.debug('Login successful, token received');
        localStorage.setItem('token', data.token);
        // ❌ REMOVED: setUser(data.user); - Auth is now handled by AuthContext
        return true;
      } else {
        const errorData = await response.text();
        logger.debug('Login failed:', { data: errorData });
        return false;
      }
    } catch (error) {
      logger.error('� Login error:', { error: getErrorMessage(error) });
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    // ❌ REMOVED: setUser(null); - Auth is now handled by AuthContext
    // ❌ REMOVED: socket.disconnect() — socket lifecycle managed by SocketContext
  }, []);

  // NEW: Step loading state management functions
  const setStepLoading = useCallback((stepId: string, loading: boolean) => {
    setStepLoadingStates(prev => ({ ...prev, [stepId]: loading }));
  }, []);

  const isStepLoading = useCallback((stepId: string) => {
    return stepLoadingStates[stepId] || false;
  }, [stepLoadingStates]);

  // PRODUCTION-READY INITIALIZATION - Single Source of Truth
  useEffect(() => {
    let mounted = true;
    
    const initializeData = async () => {
      try {
        logger.debug('PRODUCTION INIT: Starting data initialization...');
        
        // Single API call to initialize all data
        const [flowsResponse, runsResponse] = await Promise.allSettled([
          fetch(`${API_BASE_URL}/api/flows`),
          fetch(`${API_BASE_URL}/api/runs`)
        ]);
        
        // Handle flows
        if (mounted && flowsResponse.status === 'fulfilled' && flowsResponse.value.ok) {
          const flowsData = await flowsResponse.value.json();
          logger.debug(`INIT: Loaded ${flowsData.length} flows`);
          setFlows(flowsData);
        } else {
          logger.info('⚠️ INIT: No flows loaded, starting with empty state');
          setFlows([]);
        }
        
        // Handle runs
        if (mounted && runsResponse.status === 'fulfilled' && runsResponse.value.ok) {
          const runsData = await runsResponse.value.json();
          if (Array.isArray(runsData)) {
            logger.debug(`INIT: Loaded ${runsData.length} runs`);
            setRuns(runsData);
          } else {
            setRuns([]);
          }
        } else {
          logger.info('⚠️ INIT: No runs loaded, starting with empty state');
          setRuns([]);
        }
        
        logger.debug('PRODUCTION INIT: Complete');
        
      } catch (error) {
        logger.error('INIT ERROR:', { error: getErrorMessage(error) });
        // Fail gracefully - start with empty state
        if (mounted) {
          setFlows([]);
          setRuns([]);
        }
      }
    };

    initializeData();
    
    return () => {
      mounted = false;
    };
  }, []);

  // ✅ FIX: Stabilized refreshData — zero dependencies (React functional updater pattern)
  // Previously depended on [currentFlow], causing cascade: currentFlow change → refreshData recreated →
  // 8 downstream effects re-register → navigation effect loop → infinite API calls.
  // Now uses setCurrentFlow(prev => ...) to read current state without closure dependency.
  // In-flight guard prevents redundant concurrent API calls.
  const refreshData = useCallback(async () => {
    // In-flight guard: skip if already refreshing (prevents thundering herd)
    if (isRefreshingRef.current) {
      logger.debug('refreshData: skipping (already in-flight)');
      return;
    }
    isRefreshingRef.current = true;

    try {
      logger.debug('Refreshing data from backend...');
      const flowsResponse = await fetch(`${API_BASE_URL}/api/flows`);
      if (flowsResponse.ok) {
        const flowsData = await flowsResponse.json();
        logger.debug('Flows fetched from backend:', { data: flowsData.length });
        
        // Force update flows state with backend data
        setFlows(flowsData);
        
        // ✅ FIX: Use functional updater to read currentFlow without closure dependency
        // This eliminates the [currentFlow] dependency that caused the cascade loop
        setCurrentFlow(prev => {
          if (prev && !flowsData.find((f: any) => f.id === prev.id)) {
            logger.debug('Current flow no longer exists in backend, clearing...');
            return null;
          }
          return prev;
        });
      } else {
        // Stale-while-revalidate: keep existing data on transient errors (429, 5xx)
        logger.warn('Flows fetch failed, keeping existing data:', { status: flowsResponse.status });
      }

      const runsResponse = await fetch(`${API_BASE_URL}/api/runs`);
      logger.debug('Runs response status:', { data: runsResponse.status });
      if (runsResponse.ok) {
        const runsData = await runsResponse.json();
        if (Array.isArray(runsData)) {
          logger.debug('Setting runs data:', { itemCount: runsData.length });
          setRuns(runsData);
          logger.debug(`Refreshed ${runsData.length} runs, latest: ${runsData[0]?.startTime}`);
        } else {
          logger.debug('Runs data is not an array:', { data: runsData });
          setRuns([]);
        }
      } else {
        // Stale-while-revalidate: keep existing runs on transient errors
        logger.warn('Runs fetch failed, keeping existing data:', { status: runsResponse.status });
      }
    } catch (error) {
      // Network error — keep existing state (stale-while-revalidate pattern)
      logger.warn('Refresh network error, keeping existing data:', { error: getErrorMessage(error) });
    } finally {
      isRefreshingRef.current = false;
    }
  }, []); // ✅ Zero deps — this callback identity is stable forever

  // ✅ Register execution event listeners on shared socket from SocketContext
  // Socket lifecycle (create/connect/disconnect) is managed by SocketContext — NOT here
  useEffect(() => {
    if (!sharedSocket) return;

    // Join the executions room for real-time updates
    const handleConnect = () => {
      sharedSocket.emit('join-room', 'executions');
      logger.debug('[TestFlowContext] Joined executions room');
    };

    // If already connected, join immediately
    if (sharedSocket.connected) {
      handleConnect();
    }
    sharedSocket.on('connect', handleConnect);

    const handleExecutionStarted = (data: any) => {
      logger.debug('EXECUTION STARTED EVENT:', { data });
      setIsExecuting(true);
      setRuns(prev => {
        const existingIndex = prev.findIndex(run =>
          run.flowId === data.flowId && (run.id.startsWith('immediate-') || run.status === 'running')
        );
        if (existingIndex >= 0) {
          const updatedRuns = [...prev];
          updatedRuns[existingIndex] = {
            ...updatedRuns[existingIndex],
            id: data.runId || updatedRuns[existingIndex].id,
            startTime: data.timestamp || updatedRuns[existingIndex].startTime,
          };
          return updatedRuns;
        } else {
          const runningRun: TestRun = {
            id: data.runId || `run-${Date.now()}`,
            flowId: data.flowId,
            status: 'running',
            startTime: data.timestamp || new Date().toISOString(),
            logs: [],
            screenshots: []
          };
          return [runningRun, ...prev];
        }
      });
    };

    const handleExecutionProgress = (data: any) => {
      logger.debug('Execution progress', { data });
    };

    const handleExecutionCompleted = (data: any) => {
      logger.debug('EXECUTION COMPLETED EVENT:', { data });
      setIsExecuting(false);
      if (data.mode === 'real-time') {
        setRuns(prev => prev.map(run => {
          if (run.flowId === data.flowId && run.status === 'running') {
            return {
              ...run,
              id: data.runId || run.id,
              status: data.status || 'passed',
              endTime: data.endTime || new Date().toISOString(),
              duration: data.duration,
              logs: data.logs || run.logs,
              screenshots: data.screenshots || run.screenshots
            };
          }
          return run;
        }));
      }
    };

    const handleRefreshRequired = (data: any) => {
      logger.debug('SMART REFRESH TRIGGERED:', { data });
      setIsExecuting(false);
      setTimeout(() => refreshData(), 500);
    };

    const handleMonitoringModeChanged = (data: any) => {
      logger.debug('MONITORING MODE CHANGED:', { data: data.newMode });
    };

    const handleHeartbeat = (data: any) => {
      logger.debug('EXECUTION HEARTBEAT:', { durationSeconds: Math.round(data.duration / 1000) });
      setRuns(prev => prev.map(run =>
        run.flowId === data.flowId && run.status === 'running'
          ? { ...run, lastHeartbeat: data.timestamp, duration: data.duration }
          : run
      ));
    };

    const handleKpisReset = (data: any) => {
      logger.debug('KPIs RESET EVENT:', { data });
      setRuns([]);
    };

    const handleExecutionError = (data: any) => {
      logger.error('EXECUTION ERROR EVENT:', { data });
      setIsExecuting(false);
      setRuns(prev => prev.map(run => {
        if (run.flowId === data.flowId && run.status === 'running') {
          return {
            ...run,
            id: data.runId || run.id,
            status: 'failed' as const,
            endTime: data.timestamp || new Date().toISOString(),
            errorMessage: data.error
          };
        }
        return run;
      }));
    };

    // Register all listeners
    sharedSocket.on('execution-started', handleExecutionStarted);
    sharedSocket.on('execution-progress', handleExecutionProgress);
    sharedSocket.on('execution-completed', handleExecutionCompleted);
    sharedSocket.on('execution-refresh-required', handleRefreshRequired);
    sharedSocket.on('monitoring-mode-changed', handleMonitoringModeChanged);
    sharedSocket.on('execution-heartbeat', handleHeartbeat);
    sharedSocket.on('kpis-reset', handleKpisReset);
    sharedSocket.on('execution-error', handleExecutionError);

    // Cleanup: remove all listeners when socket changes or component unmounts
    return () => {
      sharedSocket.off('connect', handleConnect);
      sharedSocket.off('execution-started', handleExecutionStarted);
      sharedSocket.off('execution-progress', handleExecutionProgress);
      sharedSocket.off('execution-completed', handleExecutionCompleted);
      sharedSocket.off('execution-refresh-required', handleRefreshRequired);
      sharedSocket.off('monitoring-mode-changed', handleMonitoringModeChanged);
      sharedSocket.off('execution-heartbeat', handleHeartbeat);
      sharedSocket.off('kpis-reset', handleKpisReset);
      sharedSocket.off('execution-error', handleExecutionError);
    };
  }, [sharedSocket, refreshData]); // Re-register when socket reference changes

  const addFlow = useCallback((flow: TestFlow) => {
    setFlows(prev => [...prev, flow]);
  }, []);

  const createFlow = useCallback(async (flowData: Partial<TestFlow>): Promise<TestFlow> => {
    logger.debug('PRODUCTION CREATE: Starting flow creation...');
    
    try {
      // Generate unique ID
      const flowId = `flow-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;
      
      // Prepare complete flow data
      const completeFlowData = {
        id: flowId,
        name: flowData.name || 'Untitled Flow',
        description: flowData.description || '',
        steps: flowData.steps || [],
        rawScript: (flowData as any).rawScript || '',
        status: 'active' as const,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...flowData
      };
      
      // Optimistic update - add to UI immediately
      logger.debug(`Optimistic create: Adding ${flowId} to UI`);
      setFlows(prev => [...prev, completeFlowData as TestFlow]);
      
      // Call backend API
      logger.debug('API call: POST /api/flows');
      const response = await fetch(`${API_BASE_URL}/api/flows`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeFlowData)
      });
      
      if (!response.ok) {
        throw new Error(`Backend create failed: ${response.status} ${response.statusText}`);
      }
      
      const createdFlow = await response.json();
      logger.debug('Backend confirmed creation:', { data: createdFlow });
      
      // Update with backend response (may have additional fields)
      setFlows(prev => prev.map(flow => 
        flow.id === flowId ? createdFlow : flow
      ));
      
      logger.debug(`CREATE SUCCESS: ${flowId} created`);
      return createdFlow;
      
    } catch (error) {
      // Rollback optimistic update
      logger.error('CREATE FAILED: Rolling back', { error: getErrorMessage(error) });
      setFlows(prev => prev.filter(flow => flow.id !== flowData.id));
      throw error;
    }
  }, []);

  const cloneFlow = useCallback(async (flowId: string) => {
    logger.debug(`PRODUCTION CLONE: Starting cloning of ${flowId}`);
    
    try {
      // Step 1: Call backend API first (no optimistic update for clone)
      logger.debug(`API call: POST /api/flows/${flowId}/clone`);
      const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}/clone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`Backend clone failed: ${response.status} ${response.statusText}`);
      }
      
      const clonedFlow = await response.json();
      logger.debug('Backend created clone:', { data: clonedFlow });
      
      // Step 2: Add cloned flow to local state immediately
      logger.debug(`Adding cloned flow to UI: ${clonedFlow.id}`);
      setFlows(prev => [...prev, clonedFlow]);
      
      logger.debug(`CLONE SUCCESS: ${flowId} cloned as ${clonedFlow.id}`);
      return clonedFlow;
      
    } catch (error) {
      logger.error(`CLONE FAILED: ${flowId}`, { error: getErrorMessage(error) });
      throw error; // Re-throw for UI error handling
    }
  }, []);

  // ✅ FIX: Stabilized updateFlow — zero dependencies (React functional updater pattern)
  // Previously depended on [currentFlow?.id], causing recreation on every flow selection change.
  const updateFlow = useCallback((updatedFlow: TestFlow) => {
    logger.debug('[CONTEXT] updateFlow called:', { data: {
      flowId: updatedFlow.id,
      name: updatedFlow.name,
      hasDescription: !!updatedFlow.description
    } });
    
    setFlows(prev => prev.map(flow =>
      flow.id === updatedFlow.id ? updatedFlow : flow
    ));
    // ✅ FIX: Use functional updater to read currentFlow without closure dependency
    setCurrentFlow(prev =>
      prev?.id === updatedFlow.id ? updatedFlow : prev
    );
    
    logger.debug('[CONTEXT] Flow updated successfully');
  }, []); // ✅ Zero deps — this callback identity is stable forever

  // ✅ FIX: Stabilized deleteFlow — depends only on [refreshData] which is now stable ([] deps)
  // Previously depended on [currentFlow, refreshData], causing double instability.
  const deleteFlow = useCallback(async (flowId: string) => {
    logger.debug(`ENTERPRISE DELETE: Starting proper deletion of ${flowId}`);
    
    try {
      // Step 1: Call backend API FIRST (no optimistic updates)
      logger.debug(`API call: DELETE /api/flows/${flowId}`);
      const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      logger.debug(`Response status: ${response.status}`);
      logger.debug(`Response ok: ${response.ok}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Backend delete failed:', { data: errorText });
        throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      logger.debug('Backend confirmed deletion:', { data: result });
      
      // Step 2: Optimistic local removal BEFORE refresh
      // Ensures deleted flow disappears immediately even if refreshData() fails
      setFlows(prev => prev.filter(f => f.id !== flowId));
      
      // Step 3: Clear current flow if it was the deleted one
      // ✅ FIX: Use functional updater to avoid closure dependency on currentFlow
      setCurrentFlow(prev => {
        if (prev?.id === flowId) {
          logger.info(`🧹 Cleared current flow: ${flowId}`);
          return null;
        }
        return prev;
      });
      
      // Step 4: Sync with server (may fail on transient errors — stale-while-revalidate keeps remaining flows)
      logger.debug('Refreshing data after confirmed deletion...');
      await refreshData();
      
      logger.debug(`DELETE SUCCESS: ${flowId} deleted and UI updated`);
      
    } catch (error) {
      // Step 5: On any error, still refresh to ensure UI matches backend reality
      logger.error(`DELETE ERROR: ${error}`);
      logger.debug('Refreshing data to ensure UI consistency...');
      await refreshData();
      
      // Re-throw error for UI error handling
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error(`Failed to delete scenario: ${errorMessage}`);
    }
  }, [refreshData]); // ✅ refreshData is now stable ([] deps), so this is effectively stable too

  const clearRuns = useCallback(() => {
    logger.info('🧹 Clearing runs display (local state only - data will return on refresh)');
    setRuns([]); // Only clears local state - backend data persists
  }, []);

  const resetKPIs = useCallback(async () => {
    try {
      logger.debug('Resetting KPIs...');
      const response = await fetch(`${API_BASE_URL}/api/system/reset-kpis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        logger.debug('KPIs reset successfully:', { data: result });
        
        // Clear runs from local state immediately
        setRuns([]);
        
        // The WebSocket event will also trigger, but this ensures immediate UI update
        logger.debug(`Reset completed: ${result.deletedRuns} runs cleared, flows preserved`);
      } else {
        const error = await response.text();
        logger.error('Failed to reset KPIs:', { error: getErrorMessage(error) });
        throw new Error('Failed to reset KPIs');
      }
    } catch (error) {
      logger.error('Error resetting KPIs:', { error: getErrorMessage(error) });
      throw error;
    }
  }, []);

  const executeFlow = useCallback(async (flowId: string, options?: { recorded?: boolean }) => {
    logger.debug('[ATOMIC] EXECUTE FLOW: Starting execution for flow:', { flowId, options });

    // Add immediate running run for instant UI feedback
    const immediateRun: TestRun = {
      id: `immediate-${Date.now()}`,
      flowId: flowId,
      status: 'running',
      startTime: new Date().toISOString(),
      logs: [],
      screenshots: []
    };
    logger.debug('[ATOMIC] Adding immediate running run for UI feedback:', { data: immediateRun });
    setRuns(prev => [immediateRun, ...prev]);

    try {
      // Use the new atomic execution endpoint
      const endpoint = `${API_BASE_URL}/api/flows/${flowId}/run`;

      logger.debug('[ATOMIC] EXECUTE FLOW: Making API call to:', { data: endpoint });

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          browser: 'chromium',
          takeScreenshots: true,
          headless: false,
          userId: user?.id || null
        })
      });

      logger.debug('[ATOMIC] EXECUTE FLOW: Response status:', { data: response.status });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Execute flow error response', { status: response.status, errorText });
        throw new Error(`Failed to execute flow: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      logger.debug('[ATOMIC] EXECUTE FLOW: Execution completed:', { data: result });

      // Remove immediate run and add actual result
      setRuns(prev => [
        {
          id: result.runId,
          flowId: flowId,
          status: result.status,
          startTime: new Date(Date.now() - result.duration).toISOString(),
          endTime: new Date().toISOString(),
          duration: result.duration,
          logs: [`Execution ${result.status} in ${result.duration}ms`],
          screenshots: [],
          // Add allure report path for viewing
          allureReportPath: result.allureReportPath
        },
        ...prev.filter(run => run.id !== immediateRun.id)
      ]);

      // Store execution ID
      setCurrentExecutionId(result.runId);

      logger.debug(`[ATOMIC] Flow execution completed: ${result.status}, runId: ${result.runId}`);
      return result;

    } catch (error) {
      logger.error('[ATOMIC] EXECUTE FLOW: Execution failed:', { error: getErrorMessage(error) });
      
      // Remove immediate run and add error run
      setRuns(prev => [
        {
          ...immediateRun,
          status: 'failed',
          endTime: new Date().toISOString(),
          duration: Date.now() - new Date(immediateRun.startTime).getTime(),
          logs: [`Execution failed: ${error instanceof Error ? error.message : String(error)}`]
        },
        ...prev.filter(run => run.id !== immediateRun.id)
      ]);
      
      setIsExecuting(false);
      setCurrentExecutionId(null);
      throw error;
    }
  }, [user?.id]);

  const stopExecution = useCallback(async () => {
    try {
      if (currentExecutionId) {
        const response = await fetch(`${API_BASE_URL}/api/executions/${currentExecutionId}/stop`, {
          method: 'POST'
        });
        
        if (response.ok) {
          logger.debug('Execution stopped successfully');
        } else {
          logger.warn('Failed to stop execution via API, stopping locally');
        }
      }
    } catch (error) {
      logger.error('Error stopping execution:', { error: getErrorMessage(error) });
    } finally {
      setIsExecuting(false);
      setCurrentExecutionId(null);
    }
  }, [currentExecutionId]);

  const createFlowFromNaturalLanguage = useCallback(async (text: string): Promise<TestFlow> => {
    try {
      logger.debug('Creating flow from NL', { text });
      const response = await fetch(`${API_BASE_URL}/api/flows/create-from-nl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text }),
      });

      logger.debug('NL response status', { status: response.status });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('API Error:', { data: errorText }); // Debug log
        throw new Error(`Failed to create flow: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      logger.debug('NL API response', { result });

      const newFlow: TestFlow = {
        id: result.id,
        name: result.name,
        description: result.description,
        steps: result.steps,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt,
        status: 'draft',
      };

      addFlow(newFlow);
      return newFlow;
    } catch (error) {
      logger.error('Failed to create flow from natural language:', { error: getErrorMessage(error) });
      throw error;
    }
  }, [addFlow]);

  const value = useMemo<TestFlowContextType>(() => ({
    flows,
    currentFlow,
    runs,
    isExecuting,
    socket: sharedSocket, // ✅ From shared SocketContext (single connection per tab)
    user,
    login,
    logout,
    setFlows,
    setCurrentFlow,
    addFlow,
    createFlow,
    updateFlow,
    cloneFlow,
    deleteFlow,
    executeFlow,
    stopExecution,
    clearRuns,
    createFlowFromNaturalLanguage,
    refreshData,
    resetKPIs,
    // NEW: Step loading states for bulletproof parameter updates
    stepLoadingStates,
    setStepLoading,
    isStepLoading,
    // 🚀 NEW: Recent runs for dashboard
    fetchRecentRuns
  }), [
    flows,
    currentFlow,
    runs,
    isExecuting,
    sharedSocket,
    user,
    login,
    logout,
    addFlow,
    createFlow,
    updateFlow,
    cloneFlow,
    deleteFlow,
    executeFlow,
    stopExecution,
    clearRuns,
    createFlowFromNaturalLanguage,
    refreshData,
    resetKPIs,
    stepLoadingStates,
    setStepLoading,
    isStepLoading,
    fetchRecentRuns
  ]);

  return (
    <TestFlowContext.Provider value={value}>
      {children}
    </TestFlowContext.Provider>
  );
};
